import React from 'react';
import { Navigate } from 'react-router-dom';
import { usePermissions, PermissionsContextValue } from '../context/PermissionsContext';

type RequirePermissionProps = {
  children: React.ReactElement;
  /** Return true when the current user may view this page. */
  when: (permissions: PermissionsContextValue) => boolean;
  /** Where to send unauthorized users (defaults to their home path or My Account). */
  fallbackTo?: string;
};

/**
 * Route guard for permission-gated pages.
 * Menus already hide links; this blocks direct URL access.
 */
export const RequirePermission: React.FC<RequirePermissionProps> = ({
  children,
  when,
  fallbackTo,
}) => {
  const permissions = usePermissions();

  if (permissions.loading) {
    // Keep the current page mounted during a background permission refresh
    // (e.g. after Role Management save). Unmounting resets Settings to Locations.
    if (when(permissions)) {
      return children;
    }
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!when(permissions)) {
    const to = fallbackTo ?? permissions.defaultHomePath ?? '/my-account';
    return <Navigate to={to} replace />;
  }

  return children;
};
